import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer den Dialog "Abwesenheit hinzufuegen"
 * (`add-absence.component`, Desktop). Gestartet ueber den
 * `<app-dialog-help-button>` im Hero-Header (Pattern analog
 * `EMPLOYEE_ADD_HELP_TOUR`).
 *
 * Flaches Formular (Hero/Body/Footer, KEIN `mat-accordion`) → KEIN
 * `panelIndex`, keine Cues (ein Feld pro Schritt). Add-only (Genehmigen laeuft
 * ueber separaten Dialog) → kein Setup-Pfad, nichts zu schuetzen.
 *
 * Acht Schritte (Form-Reihenfolge; "repeat"/"credit" nur bei Berufsschule). Kernpunkte: Live-Konfliktpruefung beim
 * Zeitraum, Konto-Wirkung der Abwesenheitsarten, Antrag geht in Genehmigung.
 */
const DIALOG_CTX = { attachToDialog: true, autoCloseOnDialogClose: true };

export const ABSENCE_ADD_HELP_TOUR: TourConfig = {
  id: TourFeature.AbsenceAddHelp,
  version: 1,
  title: 'ABSENCE_ADD_HELP_TOUR_TITLE',
  description: 'ABSENCE_ADD_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Beim Start aus dem Help-Hub (kein offener Dialog): zuerst zur
  // Abwesenheits-Seite navigieren, dann den Add-Button automatisch
  // klicken. Aus dem In-Dialog-`?`-Button (Dialog schon offen) wird
  // dieser Block uebersprungen — siehe `ensureLaunchContext`.
  launchContext: {
    route: '/absence',
    opens: { openerAnchor: AnchorId.AbsenceAddButton, settleMs: 350 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 95,
  category: 'feature-discovery',
  // Mitarbeiter, Aufteilung, Wiederholen und Anrechnung entfallen je nach Zustand (skipIf). Ohne
  // dieses Flag zählte der Zähler die rohen Indizes: ohne Berufsschule sprang er „5 / 8“ → „8 / 8“
  // (Review R1, Befund 7). Die Engine zählt damit nur die sichtbaren Schritte.
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'ABSENCE_ADD_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          anchor: AnchorId.AbsenceAddDialog,
          spotlightAnchor: AnchorId.AbsenceAddDialog,
          title: 'TUTORIAL.ABSENCE_ADD.WELCOME.TITLE',
          content: 'TUTORIAL.ABSENCE_ADD.WELCOME.CONTENT',
          placement: 'right',
          dialogContext: { ...DIALOG_CTX, popoverOutsideDialog: true },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'employee',
          anchor: AnchorId.AbsenceAddEmployee,
          spotlightAnchor: AnchorId.AbsenceAddEmployee,
          title: 'TUTORIAL.ABSENCE_ADD.EMPLOYEE.TITLE',
          content: 'TUTORIAL.ABSENCE_ADD.EMPLOYEE.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Mitarbeiter-Select nur in der Manager-Ansicht sichtbar.
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="add-absence-employee"]'),
        },
        {
          id: 'daterange',
          anchor: AnchorId.AbsenceAddDateRange,
          spotlightAnchor: AnchorId.AbsenceAddDateRange,
          title: 'TUTORIAL.ABSENCE_ADD.DATERANGE.TITLE',
          content: 'TUTORIAL.ABSENCE_ADD.DATERANGE.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'daymode',
          anchor: AnchorId.AbsenceAddDayMode,
          spotlightAnchor: AnchorId.AbsenceAddDayMode,
          title: 'TUTORIAL.ABSENCE_ADD.DAYMODE.TITLE',
          content: 'TUTORIAL.ABSENCE_ADD.DAYMODE.CONTENT',
          placement: 'bottom',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Halbtags-Auswahl nur bei Eintages-Zeitraum sichtbar.
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="add-absence-daymode"]'),
        },
        {
          id: 'type',
          anchor: AnchorId.AbsenceAddType,
          spotlightAnchor: AnchorId.AbsenceAddType,
          title: 'TUTORIAL.ABSENCE_ADD.TYPE.TITLE',
          content: 'TUTORIAL.ABSENCE_ADD.TYPE.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'repeat',
          anchor: AnchorId.AbsenceAddRepeat,
          spotlightAnchor: AnchorId.AbsenceAddRepeat,
          title: 'TUTORIAL.ABSENCE_ADD.REPEAT.TITLE',
          content: 'TUTORIAL.ABSENCE_ADD.REPEAT.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          // Nur bei Art „Berufsschule“ im Dialog vorhanden.
          skipIf: () => !document.querySelector('[onboardingAnchor="add-absence-repeat"]'),
        },
        {
          id: 'credit',
          anchor: AnchorId.AbsenceAddCredit,
          spotlightAnchor: AnchorId.AbsenceAddCredit,
          title: 'TUTORIAL.ABSENCE_ADD.CREDIT.TITLE',
          content: 'TUTORIAL.ABSENCE_ADD.CREDIT.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
          skipIf: () => !document.querySelector('[onboardingAnchor="add-absence-credit"]'),
        },
        {
          id: 'save',
          anchor: AnchorId.AbsenceAddSave,
          spotlightAnchor: AnchorId.AbsenceAddSave,
          title: 'TUTORIAL.ABSENCE_ADD.SAVE.TITLE',
          content: 'TUTORIAL.ABSENCE_ADD.SAVE.CONTENT',
          placement: 'top',
          dialogContext: DIALOG_CTX,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
